<script setup lang="ts">
import { getObservationSpecieList } from "@carnet/api/backoffice";
import type { GetObservationSpecieListData } from "@carnet/api/backoffice";
import type { LocationFilter } from "@/components/WebLocationSearch.vue";
import type { SpeciesSort } from "@/composables/useSpeciesSortOptions";
import { apiBool } from "@/utils/api";
import { apiRadiusKm } from "@carnet/ui/utils/location";

const props = defineProps<{
  filter: LocationFilter;
}>();

const sort = defineModel<SpeciesSort>("sort", { required: true });
const total = defineModel<number | undefined>("total");
const loading = defineModel<boolean>("loading");

const { t } = useI18n();
const { bool, multi } = useUrlFilterValues();
const { photosTo, detailTo } = useObservationSpecieLinks();

const page = useUrlPage();

const mineOnly = useMineFilter();
const withPhotoOnly = bool("withPhoto");
const taxonIds = multi("taxons");
const featureCodes = multi("specieFeatures");
const { requestLevelCodes } = useSpecieLevelFilter();

watch(
  [
    mineOnly,
    withPhotoOnly,
    taxonIds,
    featureCodes,
    requestLevelCodes,
    () => props.filter.center?.[0],
    () => props.filter.center?.[1],
    () => props.filter.radius,
    sort,
  ],
  () => {
    page.value = 1;
  }
);

const specieQuery = computed(
  () =>
    ({
      page: page.value,
      size: 18,
      mineOnly: apiBool(mineOnly.value),
      withPhotoOnly: apiBool(withPhotoOnly.value),
      "taxonIds[]": taxonIds.value.length ? taxonIds.value.map(Number) : undefined,
      "featureCodes[]": featureCodes.value.length ? featureCodes.value : undefined,
      "specieLevelCodes[]": requestLevelCodes.value,
      sort: sort.value,
      "center[latitude]": props.filter.center?.[1],
      "center[longitude]": props.filter.center?.[0],
      radius: apiRadiusKm(props.filter),
    }) satisfies NonNullable<GetObservationSpecieListData["query"]>
);

const {
  data: speciesData,
  status: speciesStatus,
  error: speciesError,
  refresh: refreshSpecies,
} = await getObservationSpecieList({
  composable: "useLazyFetch",
  key: "observation-species",
  asyncDataOptions: { server: false },
  path: { locale: appLocale() },
  query: specieQuery,
});

const speciesList = computed(() => speciesData.value?.data?.list ?? []);
const pagination = computed(() => speciesData.value?.data?.pagination);

watch(
  pagination,
  (p) => {
    total.value = p?.total;
  },
  { immediate: true }
);

const isLoading = useIsFetching(speciesStatus);

watchEffect(() => {
  loading.value = isLoading.value;
});
</script>

<template>
  <AppSection class="bg-white grow" :ui="{ base: 'pb-30 lg:pb-25' }">
    <WebPaginatedList
      v-model:page="page"
      :items="speciesList"
      :pagination="pagination"
      :loading="isLoading"
      :error="speciesError"
      :skeleton-count="18"
      list-class="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14"
      @refresh="refreshSpecies()"
    >
      <template #skeleton><AppTileObservationSpecieSkeleton /></template>
      <template #default="{ item }">
        <AppTileObservationSpecie :specie="item" :photos-to="photosTo(item)" :detail-to="detailTo(item)" />
      </template>
    </WebPaginatedList>
  </AppSection>
</template>
